import './chrome.css';
import { type DicomJson, type InstanceRef, type SeriesRef, type StudyRef, tagString } from '../dicomweb';
import { imageIdsForSeries, initialImageIndex } from '../loader';
import { formatDicomDate, formatDicomTime } from '../overlay';
import type { ViewerShell } from '../shell';
import { isDisabledSeries, isIgnoredSeries, isUnsupportedSeries, loadStudy, UNSUPPORTED_SERIES_TITLE } from '../study';
import { loadThumbnail, THUMBNAIL_SIZE } from '../thumbnail';
import { displayKind } from '../display/kind';
import { risApiRoot } from '../config';
import { timedFetch } from '../net';
import { isCompactChrome } from './compact';

/**
 * Left drawer (spec section 1.1): " Estudios ", one collapsible block per study (the opened one
 * first, then the patient's prior studies, collapsed), series tiles with the middle-instance
 * thumbnail and a count, the "Seleccione ubicación" picker on click, drag-and-drop onto a cell,
 * multi-frame split / single-frame grouping, and the "Mostrar / Ocultar estudios" edge toggle.
 */

const LOADING_TEXT = 'Cargando ... ';
const PICKER_TITLE = 'Seleccione ubicación';
const HELP_COLLAPSE = 'Puede contraer o expandir estudios en la lista haciendo clic en el nombre del estudio.';
const DRAG_UID = 'text/series-uid';
const DRAG_KEY = 'text/series-key';
const SLICE_MODALITIES = new Set(['CT', 'MR', 'PT', 'NM']);
/** IMEXHS `splitMfSeries` / `groupSingleFrame` defaults; the user's preferences override them. */
const SPLIT_MF_SERIES = true;
const GROUP_SINGLE_FRAME = true;
let splitMfSeries = SPLIT_MF_SERIES;
let groupSingleFrame = GROUP_SINGLE_FRAME;

/** "Dividir series multi-frame" / "Agrupar instancias con una sola imagen" (Configuración → Visualización). */
export function setSeriesGrouping(options: { splitMfSeries?: boolean; groupSingleFrame?: boolean }): void {
  splitMfSeries = options.splitMfSeries ?? SPLIT_MF_SERIES;
  groupSingleFrame = options.groupSingleFrame ?? GROUP_SINGLE_FRAME;
}
const PRIOR_FIELDS = '00081030,00080020,00080030,00100020';
/** Placeholder class and caption of tiles that have no image thumbnail (spec 1.1). */
const PLACEHOLDER_TILES = { pdf: ['pdf-thumbnail', 'PDF'], video: ['video-thumbnail', 'VIDEO'], sr: ['sr-thumbnail', 'SR'] } as const;

/** One tile of the browser: a real series, or a derived subset of one (split / grouped). */
interface BrowserEntry {
  key: string;
  series: SeriesRef;
  label: string;
  derived: boolean;
  disabled: boolean;
}

const entriesByKey = new Map<string, BrowserEntry>();
const collapsedStudies = new Map<string, boolean>();
let picker: HTMLElement | null = null;

// --- Entries ------------------------------------------------------------------------------------




function seriesLabel(series: SeriesRef): string {
  return series.description || `Serie ${series.seriesNumber}`;
}

function derivedSeries(series: SeriesRef, instances: InstanceRef[]): SeriesRef {
  return { ...series, instances, instanceCount: instances.length };
}

/**
 * Tiles for one series: multi-frame instances each get their own tile (splitMfSeries) and, when a
 * series mixes them with single-frame instances, the single frames are grouped as "<desc> (S. Inst)".
 */
export function buildEntries(series: SeriesRef): BrowserEntry[] {
  if (isIgnoredSeries(series)) return [];
  const base: BrowserEntry = { key: series.seriesInstanceUID, series, label: seriesLabel(series), derived: false, disabled: isDisabledSeries(series) };
  const multi = series.instances.filter((i) => i.numberOfFrames > 1);
  const single = series.instances.filter((i) => i.numberOfFrames <= 1);
  if (!splitMfSeries || base.disabled || multi.length === 0 || series.instances.length === 1) return [base];
  const entries: BrowserEntry[] = multi.map((instance) => ({
    key: `${series.seriesInstanceUID}#${instance.sopInstanceUID}`,
    series: derivedSeries(series, [instance]),
    label: base.label,
    derived: true,
    disabled: false,
  }));
  if (single.length > 0) {
    if (groupSingleFrame) {
      entries.push({ key: `${series.seriesInstanceUID}#single`, series: derivedSeries(series, single), label: `${base.label} (S. Inst)`, derived: true, disabled: false });
    } else {
      for (const instance of single) {
        entries.push({ key: `${series.seriesInstanceUID}#${instance.sopInstanceUID}`, series: derivedSeries(series, [instance]), label: base.label, derived: true, disabled: false });
      }
    }
  }
  return entries;
}

/** Count label per series type (spec 1.1): "N Frames" / "N Cortes" / "N Instancias". */
export function countLabel(series: SeriesRef): string {
  const frames = series.instances.reduce((sum, i) => sum + i.numberOfFrames, 0);
  if (series.instances.length === 1 && frames > 1) return `${frames} Frames`;
  if (SLICE_MODALITIES.has(series.modality)) return `${frames} Cortes`;
  return `${series.instances.length} Instancias`;
}

// --- Showing an entry ---------------------------------------------------------------------------

/** Whether a viewport showing `series` is showing this entry (derived subsets are rebuilt on each render). */
function entryShows(entry: BrowserEntry, series: SeriesRef): boolean {
  if (entry.series === series) return true;
  if (!entry.derived || series.seriesInstanceUID !== entry.series.seriesInstanceUID) return false;
  return series.instances.length === entry.series.instances.length && series.instances[0]?.sopInstanceUID === entry.series.instances[0]?.sopInstanceUID;
}

/**
 * Shows an entry in grid cell `index`. Real series go through the shell; derived ones need their
 * own imageIds (the shell caches imageIds per series UID, which would mix a subset with its parent).
 */
async function showEntry(shell: ViewerShell, index: number, entry: BrowserEntry): Promise<void> {
  if (entry.disabled) return;
  if (!entry.derived || displayKind(entry.series) !== 'stack') return shell.showSeriesIn(index, entry.series);
  const viewport = shell.viewports()[index];
  if (!viewport) return;
  const imageIds = imageIdsForSeries(shell.ctx, entry.series);
  const study = shell.studies().find((s) => s.studyInstanceUID === entry.series.studyInstanceUID) ?? shell.study();
  viewport.setStudy(study);
  try {
    await viewport.showSeries(entry.series, imageIds, initialImageIndex(entry.series.modality, imageIds.length));
  } catch (error) {
    console.error('[viewer] series failed', entry.key, error);
    viewport.showError('Error al cargar la serie', 'por favor seleccione otra serie');
    return;
  }
  if (shell.viewports()[index] === viewport) shell.emit('series-shown', { index, viewport, series: entry.series });
}

// --- Grid-select picker ("Seleccione ubicación") ------------------------------------------------

function closePicker(): void {
  picker?.remove();
  picker = null;
}

function openPicker(shell: ViewerShell, entry: BrowserEntry, anchor: HTMLElement): void {
  closePicker();
  const { rows, columns } = shell.layout();
  // On the compact chrome a single cell needs no one-button picker over the overlay drawer.
  if (rows * columns === 1 && isCompactChrome()) {
    shell.guard(() => {
      shell.activate(0);
      void showEntry(shell, 0, entry);
    });
    return;
  }
  const box = document.createElement('div');
  box.className = 'grid-select';
  box.setAttribute('role', 'dialog');
  box.setAttribute('aria-label', PICKER_TITLE);
  const title = document.createElement('div');
  title.className = 'grid-select-title';
  title.textContent = PICKER_TITLE;
  const cells = document.createElement('div');
  cells.className = 'grid-select-cells';
  cells.style.gridTemplateColumns = `repeat(${columns}, auto)`;
  for (let index = 0; index < rows * columns; index++) {
    const cell = document.createElement('button');
    cell.type = 'button';
    cell.className = 'grid-select-cell';
    cell.dataset.index = String(index);
    cell.textContent = String(index + 1);
    cell.classList.toggle('active', index === shell.activeIndex());
    cell.addEventListener('click', () => {
      closePicker();
      shell.guard(() => {
        shell.activate(index);
        void showEntry(shell, index, entry);
      });
    });
    cells.appendChild(cell);
  }
  box.append(title, cells);
  shell.regions.overlayLayer.appendChild(box);
  const rect = anchor.getBoundingClientRect();
  box.style.left = `${Math.round(Math.min(rect.right + 4, window.innerWidth - box.offsetWidth - 8))}px`;
  box.style.top = `${Math.round(Math.min(rect.top, window.innerHeight - box.offsetHeight - 8))}px`;
  picker = box;
  cells.querySelector<HTMLButtonElement>('.grid-select-cell.active')?.focus();
}

function installPickerDismiss(): void {
  // A pointer press, not a mouse press: a tap on cornerstone's element synthesises no mousedown.
  document.addEventListener('pointerdown', (event) => {
    if (picker && !picker.contains(event.target as Node) && !(event.target as HTMLElement).closest?.('.series-preview')) closePicker();
  }, true);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closePicker();
  });
}

// --- Tiles and study blocks ---------------------------------------------------------------------

function seriesTile(shell: ViewerShell, entry: BrowserEntry): HTMLElement {
  const { series } = entry;
  const tile = document.createElement('button');
  tile.type = 'button';
  tile.className = 'series-preview';
  tile.dataset.seriesUid = series.seriesInstanceUID;
  tile.dataset.seriesKey = entry.key;
  tile.title = entry.label;
  tile.draggable = !entry.disabled;
  tile.disabled = entry.disabled;
  const thumb = document.createElement('img');
  thumb.width = THUMBNAIL_SIZE;
  thumb.height = THUMBNAIL_SIZE;
  thumb.alt = '';
  const loading = document.createElement('span');
  loading.className = 'series-loading';
  loading.textContent = LOADING_TEXT;
  const desc = document.createElement('span');
  desc.className = 'series-desc';
  desc.textContent = entry.label;
  const meta = document.createElement('span');
  meta.className = 'series-meta';
  meta.textContent = `${series.modality} · ${countLabel(series)}`;
  tile.append(thumb, loading, desc, meta);
  if (entry.disabled) {
    tile.classList.add('disabled', 'sr-thumbnail');
    if (isUnsupportedSeries(series)) {
      tile.classList.add('unsupported-thumbnail');
      tile.title = `${entry.label}: ${UNSUPPORTED_SERIES_TITLE}`;
      loading.textContent = 'No compatible';
    }
    return tile;
  }
  tile.addEventListener('click', () => openPicker(shell, entry, tile));
  tile.addEventListener('dragstart', (event) => {
    if (!event.dataTransfer) return;
    event.dataTransfer.setData(DRAG_KEY, entry.key);
    // Only real series use the shell's drop handler; derived tiles are placed by ours.
    if (!entry.derived) event.dataTransfer.setData(DRAG_UID, series.seriesInstanceUID);
  });
  const kind = displayKind(series);
  if (kind !== 'stack') {
    const [className, caption] = PLACEHOLDER_TILES[kind];
    tile.classList.add(className);
    tile.dataset.render = kind;
    loading.textContent = caption;
    return tile;
  }
  loadThumbnail(shell.ctx, series, thumb)
    .then(() => tile.classList.add('loaded'))
    .catch((error: unknown) => {
      tile.classList.add('no-image-thumbnail');
      console.warn('[viewer] thumbnail failed', entry.key, error);
    });
  return tile;
}

function studyDateTime(study: StudyRef): string {
  return [formatDicomDate(study.studyDate), formatDicomTime(study.studyTime)].filter(Boolean).join(' ');
}

function studyHead(study: StudyRef): HTMLElement {
  const head = document.createElement('button');
  head.type = 'button';
  head.className = 'study-head';
  head.title = study.studyDescription || HELP_COLLAPSE;
  const date = document.createElement('span');
  date.className = 'study-date';
  date.textContent = studyDateTime(study);
  const desc = document.createElement('span');
  desc.className = 'study-desc';
  desc.textContent = study.studyDescription;
  const mods = document.createElement('span');
  mods.className = 'study-mods';
  mods.textContent = [...new Set(study.series.map((s) => s.modality).filter(Boolean))].join(', ');
  head.append(date, desc, mods);
  return head;
}

function studyBlock(shell: ViewerShell, study: StudyRef, isPrior: boolean): HTMLElement {
  const block = document.createElement('section');
  block.className = 'study-block';
  block.classList.toggle('prior', isPrior);
  block.dataset.studyUid = study.studyInstanceUID;
  const uid = study.studyInstanceUID;
  if (!collapsedStudies.has(uid)) collapsedStudies.set(uid, isPrior);
  block.classList.toggle('collapsed', collapsedStudies.get(uid) === true);
  const head = studyHead(study);
  head.setAttribute('aria-expanded', String(!block.classList.contains('collapsed')));
  head.addEventListener('click', () => {
    const collapsed = block.classList.toggle('collapsed');
    collapsedStudies.set(uid, collapsed);
    head.setAttribute('aria-expanded', String(!collapsed));
  });
  const list = document.createElement('div');
  list.className = 'series-list';
  for (const series of study.series) {
    for (const entry of buildEntries(series)) {
      entriesByKey.set(entry.key, entry);
      list.appendChild(seriesTile(shell, entry));
    }
  }
  block.append(head, list);
  return block;
}

// --- Drop of derived tiles ----------------------------------------------------------------------

function installCellDrops(shell: ViewerShell): void {
  shell.viewports().forEach((viewport, index) => {
    viewport.container.addEventListener('drop', (event) => {
      const key = event.dataTransfer?.getData(DRAG_KEY);
      const entry = key ? entriesByKey.get(key) : undefined;
      if (!entry?.derived) return;
      event.preventDefault();
      shell.guard(() => {
        shell.activate(index);
        void showEntry(shell, index, entry);
      });
    });
  });
}

// --- Prior studies ------------------------------------------------------------------------------

/** Other studies of the same patient, through QIDO `studies?PatientID=`, appended collapsed. */
async function loadPriorStudies(shell: ViewerShell): Promise<void> {
  const current = shell.study();
  if (!current.patientId) return;
  const url = `${shell.config.dicomwebRoot}/studies?PatientID=${encodeURIComponent(current.patientId)}&includefield=${PRIOR_FIELDS}`;
  const response = await timedFetch(url, {
    headers: { Accept: 'application/dicom+json', ...shell.ctx.client.requestHeaders() },
    credentials: 'same-origin',
  });
  if (response.status === 204) return;
  if (!response.ok) throw new Error(`QIDO ${response.status} for patient studies`);
  const rows = (await response.json()) as DicomJson[];
  const priorUIDs = rows
    .filter((row) => tagString(row, '00100020') === current.patientId)
    .sort((a, b) => tagString(b, '00080020').localeCompare(tagString(a, '00080020')))
    .map((row) => tagString(row, '0020000D'))
    .filter((uid) => uid && !shell.studies().some((s) => s.studyInstanceUID === uid));
  for (const uid of priorUIDs) {
    try {
      const study = await loadStudy(shell.ctx.client, uid, shell.ctx.mode);
      if (study.series.length > 0) shell.addStudy(study);
    } catch (error) {
      console.warn('[viewer] prior study failed', uid, error);
    }
  }
}

// --- "Mostrar / Ocultar estudios" ---------------------------------------------------------------

function installDrawerToggle(shell: ViewerShell): void {
  const host = shell.regions.grid.parentElement;
  if (!host) return;
  const button = document.createElement('button');
  button.type = 'button';
  button.id = 'slides-navigation';
  button.className = 'slides-navigation';
  const refresh = () => {
    const open = shell.isLeftDrawerOpen();
    button.textContent = open ? '‹' : '›';
    button.title = open ? 'Ocultar estudios' : 'Mostrar estudios';
    button.setAttribute('aria-label', button.title);
  };
  button.addEventListener('click', () => {
    shell.setLeftDrawer(!shell.isLeftDrawerOpen());
    refresh();
  });
  host.appendChild(button);
  const body = shell.regions.leftDrawer.parentElement;
  if (body) new MutationObserver(refresh).observe(body, { attributes: true, attributeFilter: ['class'] });
  refresh();
}

/** Builds the drawer and keeps it in sync with the studies list and what each cell shows. */
export function install(shell: ViewerShell): void {
  const drawer = shell.regions.leftDrawer;
  const render = () => {
    closePicker();
    entriesByKey.clear();
    const title = document.createElement('h2');
    title.className = 'drawer-title';
    title.textContent = ' Estudios ';
    title.title = HELP_COLLAPSE;
    const opened = shell.study().studyInstanceUID;
    drawer.replaceChildren(title, ...shell.studies().map((study) => studyBlock(shell, study, study.studyInstanceUID !== opened)));
    markShown();
  };
  const markShown = () => {
    const shown = shell.viewports().flatMap((vp) => { const series = vp.currentStack?.series ?? vp.customSeries; return series ? [series] : []; });
    drawer.querySelectorAll<HTMLElement>('.series-preview').forEach((tile) => {
      const entry = entriesByKey.get(tile.dataset.seriesKey ?? '');
      tile.classList.toggle('shown', entry !== undefined && shown.some((series) => entryShows(entry, series)));
    });
  };
  render();
  installPickerDismiss();
  installDrawerToggle(shell);
  shell.on('studies-changed', render);
  shell.on('series-shown', markShown);
  shell.on('layout-changed', () => {
    closePicker();
    installCellDrops(shell);
    markShown();
  });
  // With ris the priors are chosen in the RIS and come on the ticket (main.ts adds them); a patient-wide
  // QIDO is outside a study-scoped ticket and ris refuses it. Only a plain PACS (bench) is searched.
  if (risApiRoot(shell.config) === null) {
    loadPriorStudies(shell).catch((error: unknown) => console.warn('[viewer] prior studies unavailable', error));
  }
}
